import * as React from "react";
import { Button } from "../../../ui/components/Button";
import { TextInput } from "../../../ui/components/Form";
import { Modal } from "../../../ui/components/Modal";
import * as authText from "../../../ui/components/Auth/authTypography.style.css";
import type { User } from "../../lib/api/users";
import {
  formatVerificationExpiry,
  getNextIncompleteTaskLabel,
  isPhoneCodeExpired,
  isPhoneSetupComplete,
} from "../../lib/auth/user-setup-progress";
import type { SetupTask } from "../../lib/auth/user-setup-progress";
import { OtpInputs, useOtpState } from "../auth/verification-inputs";
import { DevSmsCaptureLink } from "../dev/DevCaptureLinks";
import { SetupStepHeader } from "./SetupStepHeader";
import { useI18n } from "../../lib/i18n";

const COUNTRY_OPTIONS = [
  { code: "1", label: "🇺🇸 +1" },
  { code: "44", label: "🇬🇧 +44" },
  { code: "1", label: "🇨🇦 +1" },
] as const;

function formatUserPhone(user: User): string {
  if (!user.phone) return "";
  const cc = user.countryCode ? `+${user.countryCode} ` : "";
  return `${cc}${formatPhoneDisplay(user.phone)}`;
}

function formatPhoneDisplay(digits: string): string {
  const d = digits.replace(/\D/g, "");
  if (d.length <= 3) return d;
  if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6, 10)}`;
}

function stripPhoneFormatting(value: string): string {
  return value.replace(/\D/g, "").slice(0, 10);
}

export function PhoneSetupStep({
  user,
  token,
  loading,
  onError,
  onLoading,
  onRefreshUser,
  onContinueToTask,
  nextTask,
  standalone = false,
  active = true,
}: {
  user: User | null | undefined;
  token: string | null;
  loading: boolean;
  onError: (msg: string | null) => void;
  onLoading: (v: boolean) => void;
  onRefreshUser: () => Promise<void>;
  onContinueToTask?: (task: SetupTask) => void;
  nextTask?: SetupTask | null;
  standalone?: boolean;
  active?: boolean;
}) {
  const { t, tx } = useI18n();
  const panelProps = standalone
    ? {}
    : {
        role: "tabpanel" as const,
        id: "user-setup-panel-phone",
        "aria-labelledby": "user-setup-tab-phone",
      };
  const verifyButtonRef = React.useRef<HTMLButtonElement>(null);
  const phoneOtp = useOtpState({ submitRef: verifyButtonRef });
  const [countryCode, setCountryCode] = React.useState(user?.countryCode ?? "1");
  const [phone, setPhone] = React.useState(user?.phone ?? "");
  const [phoneCodeSent, setPhoneCodeSent] = React.useState(false);
  const [resendLoading, setResendLoading] = React.useState(false);
  const [removeModalOpen, setRemoveModalOpen] = React.useState(false);
  const [removeLoading, setRemoveLoading] = React.useState(false);

  React.useEffect(() => {
    if (user?.countryCode) setCountryCode(user.countryCode);
    if (user?.phone) setPhone(user.phone);
  }, [user?.countryCode, user?.phone]);

  React.useEffect(() => {
    if (active) {
      if (
        user?.phone &&
        user.phoneVerificationExpiresAt &&
        !isPhoneCodeExpired(user) &&
        !user.phoneVerified
      ) {
        setPhoneCodeSent(true);
      }
      return;
    }
    setPhoneCodeSent(false);
    setPhone(user?.phone ?? "");
    setCountryCode(user?.countryCode ?? "1");
    setRemoveModalOpen(false);
    phoneOtp.reset();
    onError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset OTP when modal closes
  }, [active, user?.phone, user?.countryCode, user?.phoneVerificationExpiresAt, user?.phoneVerified]);

  const displayPhone = phone ? `+${countryCode} ${formatPhoneDisplay(phone)}` : "your phone";
  const phoneExpired = isPhoneCodeExpired(user);
  const phoneExpiryLabel = formatVerificationExpiry(user?.phoneVerificationExpiresAt);
  const phoneExpiryText = phoneExpired
    ? t("account.setup.phone.codeExpired")
    : phoneExpiryLabel
      ? t("account.setup.phone.codeExpiresAt", { time: phoneExpiryLabel })
      : t("account.setup.phone.codeExpiresIn15m");
  const resendLabel = resendLoading
    ? t("account.common.sending")
    : phoneExpired
      ? t("account.setup.phone.sendNewCode")
      : t("account.setup.phone.resendCode");

  const handleSendPhoneCode = async () => {
    if (!token) {
      onError(t("account.common.sessionExpired"));
      return;
    }
    const phoneDigits = phone.replace(/\D/g, "");
    if (phoneDigits.length < 10) {
      onError(t("account.setup.phone.errorInvalidPhone"));
      return;
    }
    onError(null);
    onLoading(true);
    try {
      const userApi = await import("../../lib/api/users");
      const authApi = await import("../../lib/api/auth");
      await userApi.updateProfile(token, { phone: phoneDigits, countryCode });
      await authApi.requestPhoneVerification(token);
      await onRefreshUser();
      setPhoneCodeSent(true);
      phoneOtp.reset();
    } catch (e) {
      const { ApiClientError } = await import("../../lib/api/client");
      onError(e instanceof ApiClientError ? e.message : t("account.setup.phone.errorSendFailed"));
    } finally {
      onLoading(false);
    }
  };

  const handleResendPhoneCode = async () => {
    if (!token) {
      onError(t("account.common.sessionExpired"));
      return;
    }
    onError(null);
    setResendLoading(true);
    try {
      const authApi = await import("../../lib/api/auth");
      await authApi.requestPhoneVerification(token);
      await onRefreshUser();
      phoneOtp.reset();
    } catch (e) {
      const { ApiClientError } = await import("../../lib/api/client");
      onError(e instanceof ApiClientError ? e.message : t("account.setup.phone.errorResendFailed"));
    } finally {
      setResendLoading(false);
    }
  };

  const handleVerifyPhone = async () => {
    if (!token) {
      onError(t("account.common.sessionExpired"));
      return;
    }
    const code = phoneOtp.code;
    if (code.length !== 6) {
      onError(t("account.setup.phone.errorCodeRequired"));
      return;
    }
    onError(null);
    onLoading(true);
    try {
      const authApi = await import("../../lib/api/auth");
      await authApi.verifyPhoneAsUser(token, code);
      await onRefreshUser();
      phoneOtp.reset();
      setPhoneCodeSent(false);
    } catch (e) {
      const { ApiClientError } = await import("../../lib/api/client");
      onError(e instanceof ApiClientError ? e.message : t("account.setup.phone.errorVerifyFailed"));
    } finally {
      onLoading(false);
    }
  };

  const handleRemovePhone = async () => {
    if (!token) return;
    setRemoveLoading(true);
    onError(null);
    try {
      const userApi = await import("../../lib/api/users");
      await userApi.removePhone(token);
      await onRefreshUser();
      setPhone("");
      setPhoneCodeSent(false);
      phoneOtp.reset();
      setRemoveModalOpen(false);
    } catch (e) {
      const { ApiClientError } = await import("../../lib/api/client");
      onError(e instanceof ApiClientError ? e.message : t("account.setup.phone.errorRemoveFailed"));
    } finally {
      setRemoveLoading(false);
    }
  };

  if (isPhoneSetupComplete(user)) {
    return (
      <div {...panelProps} className={authText.setupStepPanel}>
        <div className={authText.setupStepHeadingGroup}>
          <div
            className={authText.stepIconCircle}
            style={{ background: "#DEF7EC", color: "#057A55", border: "1px solid #BCF0DA" }}
          >
            <span style={{ fontSize: 22, lineHeight: 1 }} aria-hidden>
              ✓
            </span>
          </div>
          <SetupStepHeader
            title={tx("account.setup.phone.verifiedTitle")}
            subtitle={
              <>
                {tx("account.setup.phone.verifiedBody")}
                <br />
                <strong className={authText.strong}>{formatUserPhone(user!)}</strong>
              </>
            }
          />
        </div>
        {!standalone && (
          <Button color="neutral" variant="outline" fullWidth onClick={() => setRemoveModalOpen(true)}>
            {tx("account.setup.phone.removePhoneNumber")}
          </Button>
        )}
        {!standalone && nextTask && onContinueToTask && (
          <Button color="primary" variant="solid" fullWidth onClick={() => onContinueToTask(nextTask)}>
            {getNextIncompleteTaskLabel(nextTask)} →
          </Button>
        )}
        {!standalone && (
        <Modal open={removeModalOpen} setOpen={setRemoveModalOpen} minWidth="320px">
          <div style={{ padding: 24 }}>
            <h2 className={authText.titleMd}>{tx("account.setup.phone.removeModalTitle")}</h2>
            <p className={authText.subtitleSpaced}>
              {tx("account.setup.phone.removeModalBody")}
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <Button
                color="primary"
                variant="solid"
                fullWidth
                loading={removeLoading}
                onClick={handleRemovePhone}
              >
                {tx("account.setup.phone.removePhone")}
              </Button>
              <Button
                color="neutral"
                variant="outline"
                fullWidth
                disabled={removeLoading}
                onClick={() => setRemoveModalOpen(false)}
              >
                {tx("shared.common.cancel")}
              </Button>
            </div>
          </div>
        </Modal>
        )}
      </div>
    );
  }

  return (
    <div
      {...panelProps}
      className={phoneCodeSent ? authText.setupStepPanel : authText.setupStepPanelForm}
    >
      {!phoneCodeSent ? (
        <>
          <SetupStepHeader
            title={tx("account.setup.phone.title")}
            subtitle={tx("account.setup.phone.subtitle")}
          />
          <div className={authText.phoneSetupFields}>
            <div className={authText.phoneRow} style={{ alignItems: "flex-end" }}>
              <div className={authText.phoneCountryWrapper}>
                <label className={authText.phoneFieldLabel}>{tx("account.setup.phone.countryLabel")}</label>
                <select
                  className={authText.countrySelect}
                  value={countryCode}
                  onChange={(e) => setCountryCode(e.target.value)}
                  aria-label={t("account.setup.phone.countryCodeAriaLabel")}
                >
                  {COUNTRY_OPTIONS.map((opt, i) => (
                    <option key={`${opt.code}-${i}`} value={opt.code}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
              <div style={{ flex: 1 }}>
                <TextInput
                  label={t("account.setup.phone.phoneNumberLabel")}
                  inputMode="tel"
                  placeholder="(555) 000-0000"
                  value={formatPhoneDisplay(phone)}
                  onChange={(e) => setPhone(stripPhoneFormatting(e.target.value))}
                />
              </div>
            </div>
            <Button
              color="primary"
              variant="solid"
              fullWidth
              size="lg"
              loading={loading}
              onClick={handleSendPhoneCode}
            >
              {tx("account.setup.phone.sendCode")}
            </Button>
          </div>
        </>
      ) : (
        <>
          <SetupStepHeader
            title={tx("account.setup.phone.verifyTitle")}
            subtitle={
              <>
                {tx("account.setup.phone.sentCodePrefix")}{" "}
                <strong className={authText.strong}>{displayPhone}</strong>
              </>
            }
          />
          <OtpInputs
            digits={phoneOtp.digits}
            refs={phoneOtp.refs}
            onInput={phoneOtp.handleInput}
            onKey={phoneOtp.handleKey}
            onPaste={phoneOtp.handlePaste}
          />
          <Button
            ref={verifyButtonRef}
            color="primary"
            variant="solid"
            fullWidth
            size="lg"
            loading={loading}
            onClick={handleVerifyPhone}
          >
            {tx("account.setup.phone.verifyButton")}
          </Button>
          <button
            type="button"
            className={authText.skipStepLink}
            style={{ marginTop: 0 }}
            onClick={() => {
              setPhoneCodeSent(false);
              setPhone("");
              setCountryCode(user?.countryCode ?? "1");
              phoneOtp.reset();
              onError(null);
            }}
          >
            {tx("account.setup.phone.useDifferentNumber")}
          </button>
          <p className={authText.emailVerifyMetaRow}>
            <span>{phoneExpiryText}</span>
            <span className={authText.emailVerifyMetaSep} aria-hidden>
              ·
            </span>
            <button
              type="button"
              className={authText.emailVerifyMetaLink}
              disabled={resendLoading || loading}
              onClick={handleResendPhoneCode}
            >
              {resendLabel}
            </button>
          </p>
          <DevSmsCaptureLink />
        </>
      )}
    </div>
  );
}
